
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {perpendicular} from "../geometry/crossSectionGeometry.js";

// Renders a top-down (plan-view) raster snapshot of the active point cloud,
// with the digitized path and its profile stations drawn on top, for the
// report's oversigtsbillede (task section 9). New code - confirmed during
// planning that no existing "render an offscreen top-view without touching
// the live camera" utility exists anywhere in this project (viewer.setTopView()
// mutates the LIVE orbit camera in place, unsuitable for a background
// snapshot).
//
// Reuses the viewer's OWN renderer/GL-context and point-cloud rendering
// pipeline (`viewer.pRenderer` - the `Renderer` class from
// src/PotreeRenderer.js, confirmed by the VR code path in viewer.js calling
// it the exact same way: `pRenderer.render(scene, camera, target, params)`)
// - never creates a second WebGL context. A throwaway THREE.OrthographicCamera
// and THREE.WebGLRenderTarget are used purely locally; the viewer's own
// active camera and canvas are never touched, and the render target is
// explicitly cleared from the renderer afterwards so the next normal frame
// renders to the canvas again as usual.
//
// `Potree.updatePointClouds(...)` is called with OUR camera right before
// rendering so the LOD/visible-node selection matches this camera's own
// frustum rather than whatever the live camera happened to have selected -
// otherwise a corridor the live camera isn't currently looking at could
// render with gaps. This only re-selects among ALREADY-LOADED octree nodes
// (the profile computation's own loadRoiPoints() call already ran and
// loaded the corridor's content by the time this runs) - it does not itself
// wait for any new asynchronous loading, which is an accepted, documented
// limitation for this overview image (see crossSectionTool.js).
//
// Orientation (user-requested, kept unchanged from the previous iteration -
// no geometric bug was found in it, only in the overlay's own visual
// styling below): the image is framed in the path's OWN rotated (u,v) frame
// (see geometry/crossSectionGeometry.js's own computeRotatedCorridorBounds()/
// computePathDirection()) rather than a fixed world/north-up one, so the
// digitized path always reads reasonably horizontally, left-to-right - u
// (the path's own overall direction) maps to the camera's local "right", v
// (perpendicular - the SAME "left of travel direction" convention the
// profile charts themselves already use) maps to the camera's local "up".
export function renderTopViewSnapshot(viewer, {rotatedBounds, polylinePoints, stations, profileLength, imageWidth = 1400}){
	const pointcloud = viewer.scene.pointclouds[0];
	const {origin, direction, perpendicular: perp, minU, maxU, minV, maxV} = rotatedBounds;
	const spanU = Math.max(maxU - minU, 1e-6);
	const spanV = Math.max(maxV - minV, 1e-6);
	const centerU = (minU + maxU) / 2;
	const centerV = (minV + maxV) / 2;

	// un-rotate the (u,v) centre back to a real world XY position for the
	// camera to sit above
	const centerX = origin[0] + centerU * direction[0] + centerV * perp[0];
	const centerY = origin[1] + centerU * direction[1] + centerV * perp[1];

	let zMin = -10, zMax = 10;
	if(pointcloud){
		const box = pointcloud.getBoundingBoxWorld();
		zMin = box.min.z;
		zMax = box.max.z;
	}
	const zMargin = Math.max((zMax - zMin) * 0.1, 1);

	const camera = new THREE.OrthographicCamera(
		-spanU / 2, spanU / 2, spanV / 2, -spanV / 2,
		0.1, (zMax - zMin) + zMargin * 2 + 0.1,
	);
	camera.position.set(centerX, centerY, zMax + zMargin);
	// camera.up = perp (v) with the camera looking straight down makes the
	// camera's own local "right" axis equal `direction` (u) - see
	// computeRotatedCorridorBounds()'s own doc comment for the derivation
	camera.up.set(perp[0], perp[1], 0);
	camera.lookAt(centerX, centerY, zMin - zMargin);
	camera.updateProjectionMatrix();
	camera.updateMatrixWorld(true);

	const imageHeight = Math.max(1, Math.round(imageWidth * (spanV / spanU)));
	const renderTarget = new THREE.WebGLRenderTarget(imageWidth, imageHeight, {
		minFilter: THREE.LinearFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat,
	});

	const renderer = viewer.renderer;
	const canvas = document.createElement("canvas");
	canvas.width = imageWidth;
	canvas.height = imageHeight;
	const ctx = canvas.getContext("2d");

	try{
		if(pointcloud && viewer.scene.pointclouds.length > 0){
			Potree.updatePointClouds(viewer.scene.pointclouds, camera, renderer);
			viewer.pRenderer.render(viewer.scene.scenePointCloud, camera, renderTarget, {});

			const raw = new Uint8Array(imageWidth * imageHeight * 4);
			renderer.readRenderTargetPixels(renderTarget, 0, 0, imageWidth, imageHeight, raw);

			// WebGL readback is bottom-up; canvas ImageData is top-down
			const flipped = new Uint8ClampedArray(raw.length);
			const rowBytes = imageWidth * 4;
			for(let row = 0; row < imageHeight; row++){
				const srcStart = row * rowBytes;
				const dstStart = (imageHeight - 1 - row) * rowBytes;
				flipped.set(raw.subarray(srcStart, srcStart + rowBytes), dstStart);
			}
			ctx.putImageData(new ImageData(flipped, imageWidth, imageHeight), 0, 0);
		}else{
			ctx.fillStyle = "#dddddd";
			ctx.fillRect(0, 0, imageWidth, imageHeight);
		}
	}finally{
		renderer.setRenderTarget(null);
		renderTarget.dispose();
	}

	// same (u,v) frame the camera itself is built from - projects any world
	// XY point to image pixels consistently, under any path orientation
	const worldToPixel = (x, y) => {
		const dx = x - origin[0], dy = y - origin[1];
		const u = dx * direction[0] + dy * direction[1];
		const v = dx * perp[0] + dy * perp[1];
		return [
			((u - minU) / spanU) * imageWidth,
			imageHeight - ((v - minV) / spanV) * imageHeight,
		];
	};

	const pixelsPerMeter = imageWidth / spanU;
	const northPixelDir = computeNorthPixelDirection(pointcloud, centerX, centerY, direction, perp);

	drawOverlay(ctx, worldToPixel, polylinePoints, stations, profileLength, perp, {
		imageWidth, imageHeight, pixelsPerMeter, northPixelDir,
	});

	return canvas;
}

// True-north direction, projected into this image's own pixel space -
// reuses the EXACT SAME projection convention already trusted elsewhere in
// this codebase for the viewer's own compass widget (see Utils.computeAzimuth
// in src/utils.js): with a known point-cloud projection, transform to WGS84
// and take a small step in increasing latitude, then transform back, so the
// resulting delta is true north in WORLD coordinates at this location;
// without a known projection, fall back to the SAME convention
// computeAzimuth itself falls back to ("assume [0, 1, 0] as north"), rather
// than inventing a new one. Returns null (arrow omitted entirely, task's
// own instruction: "en forkert eller dekorativ nordpil er værre end ingen")
// whenever the projection can't genuinely be resolved.
function computeNorthPixelDirection(pointcloud, worldX, worldY, direction, perp){
	const projection = pointcloud && pointcloud.projection;
	let northWorldDir;

	if(!projection){
		northWorldDir = [0, 1];
	}else{
		try{
			let transform;
			if(projection.includes("EPSG")){
				transform = proj4(projection, "WGS84");
			}else{
				proj4.defs("pointcloud", projection);
				transform = proj4("pointcloud", "WGS84");
			}
			const originLL = transform.forward([worldX, worldY]);
			const steppedLL = [originLL[0], originLL[1] + 1e-4];
			const steppedWorld = transform.inverse(steppedLL);
			const dx = steppedWorld[0] - worldX, dy = steppedWorld[1] - worldY;
			const len = Math.hypot(dx, dy);
			if(!isFinite(len) || len < 1e-9) return null;
			northWorldDir = [dx / len, dy / len];
		}catch(err){
			return null;
		}
	}

	// project the DIRECTION (not a point - no translation/scale, just the
	// same u/v rotation worldToPixel uses, with the same Y-flip for pixel
	// space) through the render's own (u,v) frame
	const nu = northWorldDir[0] * direction[0] + northWorldDir[1] * direction[1];
	const nv = northWorldDir[0] * perp[0] + northWorldDir[1] * perp[1];
	const pixelDx = nu, pixelDy = -nv;
	const len = Math.hypot(pixelDx, pixelDy);
	return len > 1e-9 ? [pixelDx / len, pixelDy / len] : null;
}

function rectsOverlap(a, b){
	return !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y);
}

function roundRectPath(ctx, x, y, w, h, r){
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.arcTo(x + w, y, x + w, y + h, r);
	ctx.arcTo(x + w, y + h, x, y + h, r);
	ctx.arcTo(x, y + h, x, y, r);
	ctx.arcTo(x, y, x + w, y, r);
	ctx.closePath();
}

// Strokes a polyline with a white-then-black "engraved ring" halo underneath
// its own core colour, so it stays legible against ANY background it might
// land on (dark point-cloud areas, light areas, or busy multi-coloured
// texture) - task sections 5/6/7. The main digitized vector and the profile
// tick lines use the SAME halo treatment but different core colours
// (#ffdc00 / #2f8fd6 respectively), so they stay visually distinct from
// each other while both being far more visible than a single thin stroke.
function strokePathWithHalo(ctx, points, coreColor, coreWidth){
	if(points.length < 2) return;
	const path = new Path2D();
	points.forEach(([x, y], i) => { if(i === 0) path.moveTo(x, y); else path.lineTo(x, y); });

	ctx.lineJoin = "round";
	ctx.lineCap = "round";

	ctx.strokeStyle = "rgba(255,255,255,0.95)";
	ctx.lineWidth = coreWidth + 5;
	ctx.stroke(path);

	ctx.strokeStyle = "rgba(0,0,0,0.8)";
	ctx.lineWidth = coreWidth + 2.5;
	ctx.stroke(path);

	ctx.strokeStyle = coreColor;
	ctx.lineWidth = coreWidth;
	ctx.stroke(path);
}

// P01/P02/... labels (task section 6) - a solid, semi-transparent chip
// behind bold white text reads reliably over any point-cloud content,
// avoiding the risk that a thin outline-only label still blends into a
// similarly-toned background. Labels are placed just past whichever tick
// end the overview's own "up" (perp) points toward (same convention as
// before), then pushed further out along that SAME line, in increasing
// steps, whenever the greedy placement would otherwise collide with an
// already-placed label - a simple, robust way to keep closely-spaced
// profiles' labels from overlapping without a full layout solver.
// A pure "push further along the same line" search fails to separate
// labels when many ticks are near-parallel and closely spaced (e.g. many
// profiles over a short path) - every label ends up trying to escape along
// almost the same direction as its neighbours, so they stay stacked no
// matter how far out they go. Alternating small angle offsets on top of a
// growing radius turns this into a simple spiral search, which can
// actually route around an already-placed label instead of just chasing it
// outward in a straight line. Returns {rect, placed} - `placed` is false
// only when every attempt still collided (caller falls back to a smaller
// font as a last resort, see drawStationLabels() below).
function findLabelRect(ex, ey, baseAngle, chipW, chipH, placedRects){
	let rect;
	for(let attempt = 0; attempt < 24; attempt++){
		const radius = 12 + Math.floor(attempt / 2) * 10;
		const angleStep = (attempt % 2 === 0 ? 1 : -1) * Math.ceil(attempt / 2) * (Math.PI / 10);
		const angle = baseAngle + angleStep;
		const cx = ex + Math.cos(angle) * radius, cy = ey + Math.sin(angle) * radius;
		rect = {x: cx - chipW / 2, y: cy - chipH, w: chipW, h: chipH};
		if(!placedRects.some((r) => rectsOverlap(r, rect))) return {rect, placed: true};
	}
	return {rect, placed: false};
}

function drawStationLabels(ctx, stations, worldToPixel, perp, half){
	ctx.textAlign = "center";
	ctx.textBaseline = "alphabetic";

	const placedRects = [];
	const padX = 6, padY = 4;
	// Full size first; if even the spiral search above can't find a free
	// spot (labels physically wider than the space between very closely
	// packed profiles), retry once at a smaller size rather than accepting
	// a fully unreadable overlap - a graceful degradation for an extreme
	// edge case, not the normal path.
	const FONT_SIZES = [18, 13];

	for(const station of stations){
		const localPerp = perpendicular(station.tangent);
		const ax = station.position[0] - localPerp[0] * half, ay = station.position[1] - localPerp[1] * half;
		const bx = station.position[0] + localPerp[0] * half, by = station.position[1] + localPerp[1] * half;
		const [pax, pay] = worldToPixel(ax, ay);
		const [pbx, pby] = worldToPixel(bx, by);

		const towardA = (ax - station.position[0]) * perp[0] + (ay - station.position[1]) * perp[1];
		const [ex, ey] = towardA >= 0 ? [pax, pay] : [pbx, pby];
		const [mx, my] = worldToPixel(station.position[0], station.position[1]);
		let dirX = ex - mx, dirY = ey - my;
		const dirLen = Math.hypot(dirX, dirY) || 1;
		dirX /= dirLen; dirY /= dirLen;
		const baseAngle = Math.atan2(dirY, dirX);

		let best = null, bestFontSize = FONT_SIZES[FONT_SIZES.length - 1];
		for(const fontSize of FONT_SIZES){
			ctx.font = `bold ${fontSize}px Arial, sans-serif`;
			const textWidth = ctx.measureText(station.name).width;
			const chipW = textWidth + padX * 2;
			const chipH = fontSize + padY * 2;
			const result = findLabelRect(ex, ey, baseAngle, chipW, chipH, placedRects);
			if(result.placed || !best){ best = result.rect; bestFontSize = fontSize; }
			if(result.placed) break;
		}
		placedRects.push(best);

		ctx.fillStyle = "rgba(15,15,15,0.72)";
		roundRectPath(ctx, best.x, best.y, best.w, best.h, 4);
		ctx.fill();

		ctx.font = `bold ${bestFontSize}px Arial, sans-serif`;
		ctx.fillStyle = "#ffffff";
		ctx.fillText(station.name, best.x + best.w / 2, best.y + best.h - padY);
	}
}

// Geometrically-correct graphical scale bar (task section 9) - always safe
// to draw, since `pixelsPerMeter` is a directly known quantity of this exact
// render (no separate estimate/approximation involved).
function drawScaleBar(ctx, imageWidth, imageHeight, pixelsPerMeter){
	if(!isFinite(pixelsPerMeter) || pixelsPerMeter <= 0) return;

	const NICE_METERS = [0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
	const maxBarPx = imageWidth * 0.22;
	let meters = NICE_METERS[0];
	for(const m of NICE_METERS){
		if(m * pixelsPerMeter <= maxBarPx) meters = m; else break;
	}
	const barPx = meters * pixelsPerMeter;
	const margin = 16;
	const x0 = margin + 6, y0 = imageHeight - margin - 10;

	ctx.save();
	ctx.fillStyle = "rgba(255,255,255,0.82)";
	ctx.strokeStyle = "rgba(0,0,0,0.35)";
	ctx.lineWidth = 1;
	roundRectPath(ctx, x0 - 8, y0 - 20, barPx + 16, 34, 4);
	ctx.fill();
	ctx.stroke();

	ctx.strokeStyle = "#111111";
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.moveTo(x0, y0);
	ctx.lineTo(x0 + barPx, y0);
	ctx.moveTo(x0, y0 - 5);
	ctx.lineTo(x0, y0 + 5);
	ctx.moveTo(x0 + barPx, y0 - 5);
	ctx.lineTo(x0 + barPx, y0 + 5);
	ctx.stroke();

	ctx.fillStyle = "#111111";
	ctx.font = "11px Arial, sans-serif";
	ctx.textAlign = "center";
	ctx.textBaseline = "alphabetic";
	ctx.fillText(`${meters} m`, x0 + barPx / 2, y0 - 9);
	ctx.restore();
}

// Optional north arrow (task section 9) - only drawn when
// computeNorthPixelDirection() above could genuinely resolve true north;
// otherwise the caller passes `null` and this is a no-op.
function drawNorthArrow(ctx, imageWidth, northPixelDir){
	if(!northPixelDir) return;
	const [nx, ny] = northPixelDir;
	const cx = imageWidth - 40, cy = 40;
	const len = 18, radius = len + 12;

	ctx.save();
	ctx.beginPath();
	ctx.arc(cx, cy, radius, 0, Math.PI * 2);
	ctx.fillStyle = "rgba(255,255,255,0.82)";
	ctx.fill();
	ctx.strokeStyle = "rgba(0,0,0,0.35)";
	ctx.lineWidth = 1;
	ctx.stroke();

	const tipX = cx + nx * len, tipY = cy + ny * len;
	const tailX = cx - nx * len, tailY = cy - ny * len;

	ctx.strokeStyle = "#111111";
	ctx.fillStyle = "#111111";
	ctx.lineWidth = 2.5;
	ctx.beginPath();
	ctx.moveTo(tailX, tailY);
	ctx.lineTo(tipX, tipY);
	ctx.stroke();

	const headLen = 7;
	const angle = Math.atan2(tipY - tailY, tipX - tailX);
	ctx.beginPath();
	ctx.moveTo(tipX, tipY);
	ctx.lineTo(tipX - headLen * Math.cos(angle - Math.PI / 7), tipY - headLen * Math.sin(angle - Math.PI / 7));
	ctx.lineTo(tipX - headLen * Math.cos(angle + Math.PI / 7), tipY - headLen * Math.sin(angle + Math.PI / 7));
	ctx.closePath();
	ctx.fill();

	ctx.font = "bold 12px Arial, sans-serif";
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.fillText("N", cx + nx * (radius - 8), cy + ny * (radius - 8));
	ctx.restore();
}

function drawOverlay(ctx, worldToPixel, polylinePoints, stations, profileLength, perp, {imageWidth, imageHeight, pixelsPerMeter, northPixelDir}){
	ctx.save();

	const vectorPixelPoints = polylinePoints.map((p) => worldToPixel(p[0], p[1]));
	strokePathWithHalo(ctx, vectorPixelPoints, "#ffdc00", 3.5);

	const half = profileLength / 2;
	for(const station of stations){
		// the tick's two ends, in WORLD space, at the profile's own TRUE
		// length - then projected through the SAME worldToPixel as
		// everything else, so it comes out correct under any rotation
		// without any separate "pixel-space perpendicular" computation
		const localPerp = perpendicular(station.tangent);
		const ax = station.position[0] - localPerp[0] * half, ay = station.position[1] - localPerp[1] * half;
		const bx = station.position[0] + localPerp[0] * half, by = station.position[1] + localPerp[1] * half;
		strokePathWithHalo(ctx, [worldToPixel(ax, ay), worldToPixel(bx, by)], "#2f8fd6", 3);
	}

	drawStationLabels(ctx, stations, worldToPixel, perp, half);
	drawScaleBar(ctx, imageWidth, imageHeight, pixelsPerMeter);
	drawNorthArrow(ctx, imageWidth, northPixelDir);

	ctx.restore();
}
